import React from "react";
import { Box, Typography, Skeleton, Tooltip, Stack, alpha } from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/**
 * Top-of-page KPI strip for the "Today" timetable.
 *
 * All values come from the server-computed `summary` block — the client
 * never re-tallies from `occurrences[]` so the two views cannot disagree.
 * The "By Location" card renders a live tooltip breakdown (Ajax vs
 * Scarborough vs …) instead of adding a card per location, so the strip
 * width stays constant as new locations are added.
 *
 * Visual redesign (Aug 2026): swapped white shell + tiny coloured icon
 * for a full tinted-gradient premium card so the KPI strip reads as
 * an executive command panel. Values / tooltip / handler surface
 * unchanged — this is display only.
 */

export type TodaySummary = {
  date: string;
  total_classes: number;
  total_unique_students: number;
  online_classes: number;
  in_person_classes: number;
  hybrid_classes?: number;
  makeup_classes: number;
  cancelled_classes: number;
  available_spots: number;
  students_with_outstanding: number;
  by_location: Array<{
    location_id: number | string;
    location_name: string | null;
    class_count: number;
    student_count: number;
  }>;
};

type Props = {
  summary?: TodaySummary;
  loading: boolean;
  canSeeFinance: boolean;
};

/**
 * Premium KPI card — full tinted gradient, solid icon tile, strong metric,
 * uppercase eyebrow, optional hint line. Hover lifts subtly + boosts
 * shadow. `tooltip` when present makes the card explicitly hint-able.
 */
const CardShell: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  /** Accent used for the icon tile bg, gradient wash, and typography. */
  accent: string;
  /** Optional deeper accent for the icon fg + border; falls back to accent. */
  accentDeep?: string;
  hint?: React.ReactNode;
  tooltip?: React.ReactNode;
}> = ({ label, value, icon, accent, accentDeep, hint, tooltip }) => {
  const deep = accentDeep ?? accent;
  const body = (
    <Box
      sx={{
        position: "relative",
        borderRadius: "16px",
        border: `1px solid ${alpha(deep, 0.18)}`,
        p: 1.75,
        display: "flex",
        gap: 1.25,
        alignItems: "center",
        cursor: tooltip ? "help" : "default",
        background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 55%, #ffffff 100%)`,
        boxShadow: `0 1px 3px rgba(15,23,42,0.05)`,
        transition: "transform 180ms ease-out, box-shadow 180ms ease-out",
        overflow: "hidden",
        "&::before": {
          // Soft radial glow anchored top-right — adds depth without an image.
          content: '""',
          position: "absolute",
          right: -30,
          top: -30,
          width: 90,
          height: 90,
          background: `radial-gradient(circle, ${alpha(accent, 0.22)} 0%, transparent 70%)`,
          pointerEvents: "none",
        },
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 20px ${alpha(deep, 0.16)}`,
          "& .kpi-icon-tile": { transform: "scale(1.04)" },
        },
      }}
    >
      <Box
        className="kpi-icon-tile"
        sx={{
          width: 42, height: 42, borderRadius: "12px",
          background: `linear-gradient(135deg, ${deep} 0%, ${alpha(deep, 0.75)} 100%)`,
          color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          boxShadow: `0 4px 10px ${alpha(deep, 0.35)}`,
          transition: "transform 180ms ease-out",
          "& svg": { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: deep,
          textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2,
          opacity: 0.85,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 26, fontWeight: 800, color: "#0f172a",
          lineHeight: 1.1, mt: 0.25, fontVariantNumeric: "tabular-nums",
        }}>
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 10.5, color: alpha(deep, 0.75), mt: 0.25, fontWeight: 500 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{body}</Tooltip> : body;
};

const SummaryCards: React.FC<Props> = ({ summary, loading, canSeeFinance }) => {
  const val = (n?: number) => (loading && !summary ? <Skeleton width={40} /> : n ?? 0);

  const locationTooltip = summary?.by_location?.length ? (
    <Stack spacing={0.5} sx={{ py: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
        IN-PERSON BY LOCATION
      </Typography>
      {summary.by_location.map((l) => (
        <Stack key={String(l.location_id)} direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: 12 }}>{l.location_name ?? "—"}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {l.class_count} · {l.student_count} students
          </Typography>
        </Stack>
      ))}
    </Stack>
  ) : (
    <Typography sx={{ fontSize: 12 }}>No in-person classes on this day.</Typography>
  );

  // Outstanding card flips tone when zero — mint/green when clean, red
  // when accounts flagged. Same value/handler either way; display only.
  const outstandingCount = summary?.students_with_outstanding ?? 0;
  const outstandingClean = outstandingCount === 0;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(7, 1fr)",
        },
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <CardShell
        label="Classes"
        value={val(summary?.total_classes)}
        icon={<ClassOutlinedIcon />}
        accent="#4F6EF7"
        accentDeep="#3b56d3"
        hint={summary?.cancelled_classes ? `${summary.cancelled_classes} cancelled` : "Scheduled today"}
      />
      <CardShell
        label="Students Expected"
        value={val(summary?.total_unique_students)}
        icon={<PeopleAltOutlinedIcon />}
        accent="#22A699"
        accentDeep="#0C5A46"
        hint="Unique across all classes"
      />
      <CardShell
        label="Online"
        value={val(summary?.online_classes)}
        icon={<WifiOutlinedIcon />}
        accent="#0ea5e9"
        accentDeep="#0369a1"
        hint={summary?.hybrid_classes ? `Includes ${summary.hybrid_classes} hybrid` : "Virtual sessions"}
      />
      <CardShell
        label="In-Person"
        value={val(summary?.in_person_classes)}
        icon={<LocationOnOutlinedIcon />}
        accent="#F97316"
        accentDeep="#c2410c"
        hint={
          summary?.by_location?.length
            ? summary.by_location.map((l) => `${l.location_name ?? "—"}: ${l.class_count}`).join(" · ")
            : "Onsite sessions"
        }
        tooltip={locationTooltip}
      />
      <CardShell
        label="Available Spots"
        value={val(summary?.available_spots)}
        icon={<EventAvailableOutlinedIcon />}
        accent="#22c55e"
        accentDeep="#15803d"
        hint="Open capacity"
      />
      <CardShell
        label="Makeup"
        value={val(summary?.makeup_classes)}
        icon={<AutorenewOutlinedIcon />}
        accent="#F59E0B"
        accentDeep="#b45309"
        hint="Rescheduled sessions"
      />
      {/* Non-Finance users still see the count (it's a headcount, not a
          dollar amount) — the sensitive per-invoice detail is gated in
          the row tooltip in Phase 7. Card flips to mint/green when
          zero so a clean day is celebrated rather than alarm-coloured. */}
      <CardShell
        label="Outstanding"
        value={val(outstandingCount)}
        icon={outstandingClean ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
        accent={outstandingClean ? "#22c55e" : "#EF5350"}
        accentDeep={outstandingClean ? "#15803d" : "#b91c1c"}
        hint={outstandingClean ? "All accounts clear" : canSeeFinance ? "With unpaid invoice" : "Flagged accounts"}
      />
    </Box>
  );
};

export default SummaryCards;
